import axios from "axios";
import { Proposal, ProposalResponse, ApiResponse } from "../types";
import { apiUrl } from "../config/apiBase";

// apiUrl() is read per call rather than once at module load: which API to talk
// to depends on the link the customer followed, so it belongs to the request.

// New email-based API functions using encrypted_id
export const fetchProposalByEmail = async (
  encryptedId: string,
): Promise<Proposal | null> => {
  try {
    const response = await axios.get(
      `${apiUrl()}/proposal/email/show?encrypted_id=${encryptedId}`,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    console.log("API Response:", response.data);

    // Handle the actual response structure where proposal is directly in the response
    if (response.data && response.data.proposal) {
      const proposal = response.data.proposal;
      // Merge display_title from API (uses company name when template is "Clean Cinch" or empty)
      const displayTitle = response.data.display_title;
      return displayTitle != null
        ? { ...proposal, display_title: displayTitle }
        : proposal;
    }

    // Fallback: check if it's wrapped in success/data structure
    if (response.data.success && response.data.data) {
      const proposal = response.data.data;
      const displayTitle = response.data.display_title;
      return displayTitle != null
        ? { ...proposal, display_title: displayTitle }
        : proposal;
    }

    return null;
  } catch (error: any) {
    console.error("Error fetching proposal by email:", error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw new Error("Failed to fetch proposal data");
  }
};

export const approveProposalByEmail = async (
  encryptedId: string,
  signature: string,
): Promise<ApiResponse<ProposalResponse>> => {
  try {
    // Send the customer's own browser-local date so the approval date
    // stamped on the signed PDF reflects their timezone, not the server's —
    // same local_date convention already used for geofence check-ins.
    const d = new Date();
    const local_date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

    const response = await axios.post<ApiResponse<ProposalResponse>>(
      `${apiUrl()}/proposal/email/approve?encrypted_id=${encryptedId}`,
      { signature, local_date },
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error("Error approving proposal by email:", error);
    throw new Error("Failed to approve proposal");
  }
};

export const rejectProposalByEmail = async (
  encryptedId: string,
): Promise<ApiResponse<ProposalResponse>> => {
  try {
    const response = await axios.post<ApiResponse<ProposalResponse>>(
      `${apiUrl()}/proposal/email/reject?encrypted_id=${encryptedId}`,
      {},
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error("Error rejecting proposal by email:", error);
    throw new Error("Failed to reject proposal");
  }
};
